// components/common/modal/VersionWelcomeModal.tsx
import React from 'react';
import { Box, Button, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import GroupsIcon from '@mui/icons-material/Groups';
import InsightsIcon from '@mui/icons-material/Insights';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { BaseModal } from './BaseModal';
import { ModalTitle } from './ModalTitle';
import { ModalActions } from './ModalActions';
import { ModalContent } from './ModalContent';
import releaseNotes from '@/content/releaseNotes.json';

export const RELEASE_TAG = releaseNotes.releaseTag;

const ICON_MAP: Record<string, React.ReactNode> = {
    ai: <SmartToyIcon color="primary" />,
    bookings: <VideoLibraryIcon color="primary" />,
    chameleon: <SwapHorizIcon color="primary" />,
    dashboard: <DashboardIcon color="primary" />,
    help: <LightbulbIcon color="primary" />,
    media: <GraphicEqIcon color="primary" />,
    reach: <TrendingUpIcon color="primary" />,
    uplift: <InsightsIcon color="primary" />,
    users: <GroupsIcon color="primary" />,
    workflow: <TuneIcon color="primary" />,
};

interface VersionWelcomeModalProps {
    open: boolean;
    onDismiss: () => void;
}

export const VersionWelcomeModal = ({ open, onDismiss }: VersionWelcomeModalProps) => {
    return (
        <BaseModal open={open} onClose={onDismiss} maxWidth="sm">
            <ModalTitle icon={NewReleasesIcon} onClose={onDismiss}>
                {releaseNotes.title}
            </ModalTitle>
            <ModalContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {releaseNotes.greeting}
                </Typography>
                <List disablePadding>
                    {releaseNotes.highlights.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                {ICON_MAP[item.icon] ?? <NewReleasesIcon color="primary" />}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                secondary={item.detail}
                                slotProps={{
                                    primary: { variant: 'body2', sx: { fontWeight: 600 } },
                                    secondary: { variant: 'body2' },
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {releaseNotes.footer}
                    </Typography>
                </Box>
            </ModalContent>
            <ModalActions>
                <Box />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onDismiss}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Got it
                </Button>
            </ModalActions>
        </BaseModal>
    );
};
